import React, { useCallback, useContext, useEffect, useState } from "react"
import applyMigrations, { DB_VERSION } from "./migrations"
import { Alert, Button, ProgressBar } from "react-bootstrap"
import { UserContext } from "../components/auth/auth";
import { ENTITY, OBJECT_TYPE } from "../eventsDispatcher/eventTypes";
import useWheelId from "../hooks/useWheelId";
import styles from "../components/auth/LoginForm.module.css";

export interface DatabaseContextData {
    db: IDBDatabase
    putObject: (object_type: OBJECT_TYPE, data: ENTITY) => Promise<null>
    getObject: (object_type: OBJECT_TYPE, uuid: string) => Promise<ENTITY | null>
    updateObject: (object_type: OBJECT_TYPE, uuid: string, data: Partial<ENTITY>) => Promise<ENTITY>
    deleteObject: (object_type: OBJECT_TYPE, uuid: string) => Promise<null>
}


export const DataBaseContext = React.createContext<DatabaseContextData>({
    // @ts-ignore
    db: null,
    putObject: async (object_type: OBJECT_TYPE, data: ENTITY) => null,
    getObject: async (object_type: OBJECT_TYPE, uuid: string) => null,
    // @ts-ignore
    updateObject: async (object_type: OBJECT_TYPE, uuid: string, data: Partial<ENTITY>) => null,
    deleteObject: async (object_type: OBJECT_TYPE, uuid: string) => null,
});

interface StatusPageProps {
    title: string;
    message: string;
    variant: 'danger' | 'info';
    onAction?: () => void;
    actionLabel?: string;
}

const StatusPage: React.FC<StatusPageProps> = ({ title, message, variant, onAction, actionLabel }) => (
    <div className={styles.container}>
        <div className={styles.loginCard}>
            <div className={styles.logoSection}>
                <img
                    src="/splash400.png"
                    alt="Artifacts"
                    className={styles.logo}
                    style={{ width: 200, height: 200, objectFit: 'contain' }}
                />
            </div>
            <h2 className={styles.title}>{title}</h2>
            <div className={styles.form}>
                <Alert variant={variant}>
                    <p>{message}</p>
                    {onAction && (
                        <Button variant={variant} onClick={onAction}>
                            {actionLabel || 'OK'}
                        </Button>
                    )}
                </Alert>
            </div>
        </div>
    </div>
);

const LoadingPage: React.FC<{ title: string }> = ({ title }) => (
    <div className={styles.container}>
        <div className={styles.loginCard}>
            <div className={styles.logoSection}>
                <img
                    src="/splash400.png"
                    alt="Artifacts"
                    className={styles.logo}
                    style={{ width: 200, height: 200, objectFit: 'contain' }}
                />
            </div>
            <h2 className={styles.title}>{title}</h2>
            <div className={styles.form}>
                <Alert variant="info">
                    <p>Загрузка приложения...</p>
                    <ProgressBar style={{ height: 20 }} variant="info" striped animated min={0} max={100} now={100} />
                </Alert>
            </div>
        </div>
    </div>
);

export const DatabaseProvider: React.FC<React.PropsWithChildren> = ({ children }) => {

    const wheelId = useWheelId()

    const { impersonate_code } = useContext(UserContext);
    const dbName = wheelId === 'my' ? impersonate_code : wheelId
    const [db, setDb] = useState<IDBDatabase>()
    const [error, setError] = useState("");
    const [update, setUpdate] = useState(false);
    const putObject = useCallback((object_type: OBJECT_TYPE, data: ENTITY): Promise<null> => {
        return new Promise((resolve, reject) => {
            if (!db)
                reject(new Error('db not initiated'))
            else {

                const transaction = db.transaction([object_type], 'readwrite');
                const store = transaction.objectStore(object_type);
                let request = store.put(data);
                request.onsuccess = () => resolve(null);
                request.onerror = () => reject(request.error);
            }

        })
    }, [db]);

    const getObject = useCallback((object_type: OBJECT_TYPE, uuid: string): Promise<ENTITY | null> => {
        return new Promise((resolve, reject) => {
            if (!db)
                reject(new Error('db not initiated'))
            else {
                const transaction = db.transaction([object_type], 'readonly');
                const store = transaction.objectStore(object_type);
                const request = store.get(uuid);
                request.onsuccess = () => resolve(request.result || null);
                request.onerror = () => reject(request.error);
            }
        })
    }, [db]);

    const updateObject = useCallback(async (object_type: OBJECT_TYPE, uuid: string, data: Partial<ENTITY>): Promise<ENTITY> => {
        let lastObj = await getObject(object_type, uuid) || {};
        let newObj = { ...lastObj, uuid, ...data };
        await putObject(object_type, newObj as ENTITY);
        return newObj as ENTITY;
    }, [db]);

    const deleteObject = useCallback((object_type: OBJECT_TYPE, uuid: string): Promise<null> => {
        return new Promise((resolve, reject) => {
            if (!db)
                reject(new Error('db not initiated'))
            else {
                const transaction = db.transaction([object_type], 'readwrite');
                const store = transaction.objectStore(object_type);
                let request = store.delete(uuid);
                request.onsuccess = () => resolve(null);
                request.onerror = () => reject(request.error);
            }
        })
    }, [db]);

    useEffect(() => {
        console.log('setup db')
        if (impersonate_code === undefined) {
            console.log('wait for user')
            return
        }
        if (dbName === undefined) {
            console.log('wait for user')
            return
        }
        if (!window.indexedDB) {
            setError('Ваш браузер не поддерживает IndexedDB. К сожалению приложение не получится запустить.');
            return;
        }
        let request;
        try {
            request = indexedDB.open(dbName, DB_VERSION);
        } catch (error) {
            setError(`Не удалось открыть базу данных`)
            return
        }

        request.onerror = () => {
            setError(`Ошибка при открытии базы`);
            return
        };

        request.onsuccess = (event) => {
            // @ts-ignore
            const db: IDBDatabase = event.target.result;

            // Добавляем обработчики ошибок для базы данных
            db.onerror = (event) => {
                setError(`Ошибка работы базы данных`);

            };

            db.onversionchange = () => {
                setUpdate(true);
                db.close();
                setDb(undefined);
            };

            setDb(db);

        };

        // Обработка обновления версии
        request.onupgradeneeded = (event) => {
            // @ts-ignore
            const db: IDBDatabase = event.target.result;
            setUpdate(true);
            try {
                // @ts-ignore
                applyMigrations(db, event.oldVersion, event.newVersion || 1, event.target.transaction)
            } catch (error) {
                setError(`Ошибка при обновлении базы "${dbName}": ${error}`);
                return

            } finally {
                setUpdate(false);
            }
        };

    }, [dbName]);
    if (error !== "") {
        return <StatusPage
            title="Ошибка"
            message={error}
            variant="danger"
            onAction={() => window.location.reload()}
            actionLabel="Перезагрузить страницу"
        />
    }

    if (update) {
        return <LoadingPage title="Обновление" />
    }
    if (db == null) {
        return <LoadingPage title="Загрузка приложения" />
    }
    return <DataBaseContext.Provider value={{ db: db, putObject, getObject, updateObject, deleteObject }}>
        {children}
    </DataBaseContext.Provider>
}